import * as RadixCheckbox from '@radix-ui/react-checkbox';
import classNames from 'classnames';
import { memo, useId } from 'react';

import { type Color } from '@air/api/types';
import { Check } from '@air/next-icons';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

import { getContrastColor } from '~/components/v2/utils/filters/getContrastColor';
import { type ColorToggleParams } from '../ColorFilterCard';

export type DefaultColorFilterControlProps = {
  colors: Color[];
  onClick: (params: ColorToggleParams) => void;
  selectedColors: string[];
};

export const DefaultColorFilterControl = memo(({ colors, onClick, selectedColors }: DefaultColorFilterControlProps) => {
  const id = useId();

  return (
    <div className="flex flex-col gap-2" data-testid="DEFAULT_COLOR_FILTER_CONTROL">
      <p className="text-12 text-grey-12">Default colors</p>
      <div className="flex flex-wrap gap-1">
        {colors.map((color) => {
          const isSelected = selectedColors.includes(color.hex);
          const isWhite = color.name === 'white';

          return (
            <label key={color.hex} htmlFor={id}>
              <RadixCheckbox.Root
                className="flex size-6 items-center justify-center rounded-24 border border-grey-5 bg-grey-1 ring-0 ring-transparent ring-inset transition-colors hover:bg-grey-4 data-[state=checked]:border-grey-10 data-[state=checked]:ring-1 data-[state=checked]:ring-grey-10"
                checked={isSelected}
                id={id}
                data-testid="COLOR_FILTER_CHECKBOX"
                data-color-name={color.name}
                onCheckedChange={(checked) => onClick({ color, isSelected: !!checked })}
              >
                <div
                  className={classNames(
                    'flex size-4 items-center justify-center rounded-full',
                    isWhite && 'border border-grey-5',
                  )}
                  style={{ backgroundColor: color.hex }}
                >
                  <RadixCheckbox.Indicator>
                    <Check className="size-3" style={{ color: getContrastColor(color.hex) }} />
                  </RadixCheckbox.Indicator>
                </div>
              </RadixCheckbox.Root>
              <VisuallyHidden>Select {color.name}</VisuallyHidden>
            </label>
          );
        })}
      </div>
    </div>
  );
});

DefaultColorFilterControl.displayName = 'DefaultColorFilterControl';
